import { useCallback, useEffect, useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { faMagnifyingGlass, faRectangleList, faStar } from "@fortawesome/pro-regular-svg-icons"
import { faStar as faStarSolid, faXmark } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { DialogContent, IconButton, Stack, Typography } from "@mui/material"
import { useQueryClient } from "@tanstack/react-query"

import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { escapeRegExp } from "../../../../../../lib/stringLib"
import { tailwindPalette } from "../../../../../../modules/theme/tailwindPalette"
import { StandardButton } from "../../../../buttons/StandardButton"
import { StandardDialog } from "../../../../dialogs/StandardDialog"
import { EmptyContent } from "../../../../skeleton/EmptyContent"
import { SelectedPriceListItem } from "../../../createOrderTypes"
import { FilterSearch } from "../../FilterSearch"

import { MeetingServicePriceListsQuery, savePriceListFavorite } from "./priceListsApi"
import type { MeetingServicePriceListsResponse } from "./priceListsApi"
import type { MeetingServicePriceListsItem } from "./priceListsTypes"
import { MerchantPriceListsGroup } from "./MerchantPriceListsGroup"
import { filterPriceList } from "./helpers"

const useStyles = makeStyles()((theme) => ({
    dialogContent: {
        // the sticky header and footer bleed to the dialog edges, so padding lives on the sections
        padding: "0",
        display: "flex",
        flexDirection: "column",
        color: theme.palette.tailwind.slate[950],
        minHeight: "28rem",
    },
    header: {
        position: "sticky",
        top: 0,
        zIndex: 10,
        padding: "1.5rem 2.5rem 1rem",
        backgroundColor: theme.palette.common.white,
    },
    title: {
        fontWeight: 700,
    },
    subtitle: {
        color: theme.palette.tailwind.slate[500],
    },
    closeButton: {
        "&&": {
            width: "2.375rem",
            height: "2.375rem",
            borderRadius: ".6875rem",
            fontSize: "1.0625rem",
            backgroundColor: theme.palette.tailwind.slate[100],
            color: theme.palette.tailwind.slate[600],
        },
        "&&:hover": {
            backgroundColor: theme.palette.tailwind.slate[200],
        },
    },
    body: {
        flexGrow: 1,
        padding: "1.5rem 2.5rem",
    },
    footer: {
        position: "sticky",
        bottom: 0,
        zIndex: 10,
        padding: "1rem 2.5rem",
        backgroundColor: theme.palette.common.white,
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    footerNote: {
        color: theme.palette.tailwind.slate[500],
    },
    // reshapes FilterSearch's outlined field into the flat pill from the design;
    // doubled specificity so these win over the component's own descendant rules
    searchField: {
        "&& .MuiOutlinedInput-root": {
            height: "2.875rem",
            borderRadius: ".75rem",
            backgroundColor: theme.palette.tailwind.slate[100],
            boxShadow: "none",
        },
        "&& .MuiOutlinedInput-notchedOutline": {
            border: "none",
        },
        // both in-field buttons are rounded squares, not the default MUI circles.
        // Shape only - the hover colours stay on the toggle's own classes, otherwise this
        // descendant selector would outrank them and paint the active star grey.
        "&& .MuiInputAdornment-positionEnd .MuiIconButton-root": {
            borderRadius: ".5625rem",
            padding: ".375rem",
        },
    },
    favoritesToggle: {
        "&&": {
            color: theme.palette.tailwind.slate[400],
        },
        "&&:hover": {
            backgroundColor: theme.palette.tailwind.slate[200],
        },
    },
    favoritesToggleActive: {
        "&&": {
            color: theme.palette.tailwind.amber[500],
            backgroundColor: theme.palette.tailwind.amber[50],
        },
        "&&:hover": {
            backgroundColor: theme.palette.tailwind.amber[100],
        },
    },
    noEdge: {
        height: 0,
    },
    emptyWrap: {
        flex: 1,
        justifyContent: "center",
    },
}))
export type SelectPriceListsFiltersTypes = Readonly<{
    showFavoritesOnly: boolean
    search: string
}>

type SelectPriceListsDialogProps = Readonly<{
    userId: number
    onClose: () => void
    merchants: MeetingServicePriceListsItem[]
    onSelect: (item: SelectedPriceListItem) => void
}>

export function SelectPriceListsDialog({ onClose, merchants, userId, onSelect }: SelectPriceListsDialogProps) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    // the pick is only committed when Continue is pressed, so it stays local until then
    const [selectedItem, setSelectedItem] = useState<SelectedPriceListItem | null>(null)
    const [filters, setFilters] = useState<SelectPriceListsFiltersTypes>(() => {
        const hasFavorites = merchants.some((merchant) => merchant.priceLists.some((pl) => pl.isFavorite))
        return { showFavoritesOnly: hasFavorites, search: "" }
    })

    // the search text is escaped: unescaped input like "(" throws while building the regex
    const searchRegex = useMemo(
        () => (filters.search ? new RegExp(`(${escapeRegExp(filters.search)})`, "i") : undefined),
        [filters.search],
    )

    const filteredMerchants = useMemo(
        () => filterPriceList(filters.showFavoritesOnly, searchRegex, merchants),
        [merchants, searchRegex, filters.showFavoritesOnly],
    )

    const favoriteItemsCount = useMemo(
        () =>
            merchants.reduce((count, merchant) => count + merchant.priceLists.filter((pl) => pl.isFavorite).length, 0),
        [merchants],
    )
    useEffect(() => {
        if (favoriteItemsCount === 0) {
            setFilters((prev) => ({ ...prev, showFavoritesOnly: false }))
        }
    }, [favoriteItemsCount])

    const totalPriceListsCount = useMemo(
        () => merchants.reduce((count, merchant) => count + merchant.priceLists.length, 0),
        [merchants],
    )

    const queryClient = useQueryClient()

    const { mutate: callSavePriceListFavorite } = useApiMutation(savePriceListFavorite, {})

    const handleFavoriteClick = (merchantId: number, priceListId: number) => {
        queryClient.setQueriesData<MeetingServicePriceListsResponse>(
            { queryKey: [MeetingServicePriceListsQuery.USER_MEETING_SERVICE_PRICE_LISTS] },
            (oldData) => {
                if (!oldData) return oldData
                return {
                    ...oldData,
                    merchants: oldData.merchants.map((m) =>
                        m.id !== merchantId
                            ? m
                            : {
                                  ...m,
                                  priceLists: m.priceLists.map((pl) =>
                                      pl.id !== priceListId ? pl : { ...pl, isFavorite: !pl.isFavorite },
                                  ),
                              },
                    ),
                }
            },
        )

        callSavePriceListFavorite({
            userId,
            merchants: [
                {
                    id: merchantId,
                    priceList: {
                        id: priceListId,
                    },
                },
            ],
        })
    }
    const handleSearch = useCallback((searchText: string) => {
        setFilters((prev) => ({ ...prev, search: searchText }))
    }, [])

    const toggleFavoritesFilter = useCallback(() => {
        setFilters((prev) => ({ ...prev, showFavoritesOnly: !prev.showFavoritesOnly }))
    }, [])

    // the counts live in the tooltip now that the toggle is icon-only
    const favoritesToggleLabel = filters.showFavoritesOnly
        ? l10n.getString("create-order-select-price-list-favorites-view-all", { count: totalPriceListsCount })
        : l10n.getString("create-order-select-price-list-favorites-section", { count: favoriteItemsCount })

    // onSelect closes the dialog itself, so there is no onClose call here
    const handleContinue = useCallback(() => {
        if (selectedItem) onSelect(selectedItem)
    }, [onSelect, selectedItem])

    const hasAnyPriceLists = totalPriceListsCount > 0

    return (
        <StandardDialog
            open
            onClose={onClose}
            maxWidthProp="lg"
            scroll="paper"
            topbar={<></>}
            updateEdge={classes.noEdge}
        >
            <DialogContent className={classes.dialogContent}>
                <Stack gap="1rem" className={classes.header}>
                    <Stack direction="row" gap="1rem" alignItems="flex-start" justifyContent="space-between">
                        <Stack gap=".125rem">
                            <LocalizedStrict id="create-order-select-price-list-dialog">
                                <Typography variant="h5" className={classes.title}>
                                    Select price list
                                </Typography>
                            </LocalizedStrict>
                            <LocalizedStrict id="create-order-select-price-list-dialog-subtitle">
                                <Typography variant="body2" className={classes.subtitle}>
                                    Meeting catering
                                </Typography>
                            </LocalizedStrict>
                        </Stack>
                        <IconButton
                            onClick={onClose}
                            className={classes.closeButton}
                            title={l10n.getString("create-order-select-price-list-close-button")}
                            aria-label={l10n.getString("create-order-select-price-list-close-button")}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </IconButton>
                    </Stack>
                    <FilterSearch
                        search={filters.search}
                        setSearch={handleSearch}
                        defaultActive
                        activeWidth="100%"
                        className={classes.searchField}
                        placeholderId="create-order-select-price-list-search-placeholder"
                        trailingAction={
                            <IconButton
                                size="small"
                                className={cx(
                                    classes.favoritesToggle,
                                    filters.showFavoritesOnly && classes.favoritesToggleActive,
                                )}
                                onClick={toggleFavoritesFilter}
                                disabled={favoriteItemsCount === 0}
                                aria-pressed={filters.showFavoritesOnly}
                                title={favoritesToggleLabel}
                                aria-label={favoritesToggleLabel}
                            >
                                <FontAwesomeIcon icon={filters.showFavoritesOnly ? faStarSolid : faStar} />
                            </IconButton>
                        }
                    />
                </Stack>

                <Stack gap="2rem" className={classes.body}>
                    {filteredMerchants.length > 0 ? (
                        filteredMerchants.map((merchant) => (
                            <MerchantPriceListsGroup
                                merchant={merchant}
                                onSelect={setSelectedItem}
                                searchRegex={searchRegex}
                                selectedItem={selectedItem}
                                onToggleFavorite={handleFavoriteClick}
                                key={merchant.id}
                            />
                        ))
                    ) : (
                        <Stack className={classes.emptyWrap}>
                            <EmptyContent
                                icon={hasAnyPriceLists ? faMagnifyingGlass : faRectangleList}
                                backgroundColor={tailwindPalette.slate[400]}
                                title={l10n.getString(
                                    hasAnyPriceLists
                                        ? "create-order-select-price-list-no-search-results"
                                        : "create-order-select-price-list-no-price-lists-title",
                                )}
                                description={
                                    hasAnyPriceLists
                                        ? l10n.getString("create-order-select-price-list-no-search-results-description")
                                        : undefined
                                }
                            />
                        </Stack>
                    )}
                </Stack>

                <Stack
                    direction="row"
                    gap="1rem"
                    alignItems="center"
                    justifyContent="space-between"
                    className={classes.footer}
                >
                    {selectedItem ? (
                        <LocalizedStrict
                            id="create-order-select-price-list-footer-selected"
                            vars={{ priceList: selectedItem.priceListName, merchant: selectedItem.merchantName }}
                        >
                            <Typography variant="body2" noWrap className={classes.footerNote}>
                                {selectedItem.priceListName} · {selectedItem.merchantName}
                            </Typography>
                        </LocalizedStrict>
                    ) : (
                        <LocalizedStrict id="create-order-select-price-list-footer-empty">
                            <Typography variant="body2" noWrap className={classes.footerNote}>
                                Choose a price list
                            </Typography>
                        </LocalizedStrict>
                    )}
                    <LocalizedStrict id="create-order-select-price-list-continue-button">
                        <StandardButton
                            variant="contained"
                            fullWidth={false}
                            disabled={!selectedItem}
                            onClick={handleContinue}
                        >
                            Continue
                        </StandardButton>
                    </LocalizedStrict>
                </Stack>
            </DialogContent>
        </StandardDialog>
    )
}
